import * as React from 'react'
import Dropzone from 'react-dropzone'

import { Avatar, EnhancedDimension, Icon, Tooltip } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import createEffect from 'modules/core/hooks/createEffect'
import buildUrl from 'modules/core/lib/buildUrl'

import styles from './index.module.scss'

type UploaderSizes = 's120' | 's80'

interface Props {
  avatarField: Field
  readonly?: boolean
  size?: EnhancedDimension & UploaderSizes
  handleDrop?: () => void
}

const cx = classNames.bind(styles)

const setAvatar = createEffect((avatarField) => {
  if (avatarField.errors && avatarField.errors.length > 0) {
    avatarField.set(null)
  }
})

const AvatarUploader = ({ avatarField, readonly, size = 's120', handleDrop }: Props) => {
  setAvatar(avatarField)

  const onDrop = (files: File[]) => {
    avatarField.set(files[0])
    handleDrop && handleDrop()
  }

  const error = (): string | null | undefined => {
    if (!avatarField.isDirty && avatarField.errors) {
      return avatarField.errors[0]
    } else {
      return null
    }
  }

  const Image = () => {
    return <img src={`${URL.createObjectURL(avatarField.value)}`} className={styles.image} />
  }

  const renderFile = (illustration: React.ComponentProps<typeof Icon.Large>['icon']) => {
    if (!avatarField.value) {
      return (
        <div className={styles.illustration}>
          <Icon.Large icon={illustration} />
        </div>
      )
    }

    if (avatarField.isDirty) {
      return (
        <Tooltip attachment="bottom center" type="wrong" open={Boolean(error())}>
          <Image />
          <Tooltip.Pad>{error()}</Tooltip.Pad>
        </Tooltip>
      )
    }

    return (
      <div className={styles.avatar}>
        <Avatar url={buildUrl(avatarField.value)} size={size} label="" />
        <div className={styles.hover} />
      </div>
    )
  }

  if (readonly) {
    return <div className={cx('avatarReadonly', size)}>{renderFile('add-avatar')}</div>
  }

  return (
    <Dropzone onDrop={onDrop} multiple={false}>
      {({ getRootProps, getInputProps }) => (
        <div {...getRootProps()} className={cx('avatarUploader', size)}>
          <input {...getInputProps()} />
          {renderFile('add-avatar')}
        </div>
      )}
    </Dropzone>
  )
}

export default observer(AvatarUploader)
